import { Injectable, Inject } from "@angular/core";
import { BehaviorSubject, Observable, map } from "rxjs";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { OKTA_AUTH } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";
import { AppConfigService } from "src/app/services/app-config.service";
import {
  DashboardResponseSchema,
  DashboardResponse,
  ProspectDetails,
  ProspectDetailsSchema,
  FiltersResponse,
  FiltersResponseSchema,
} from "./dashboard.schemas";

export interface DashboardState {
  pageIndex: number;
  pageSize: number;
  sortingColumn: string | null;
  sortingOrder: "asc" | "desc" | "" | null;
  searchText: string;
  filterSelected: any;
  selectedWidget: string;
}
@Injectable({
  providedIn: "root",
})
export class DashboardService {
  private readonly filterSubject = new BehaviorSubject<any>({
    program: [],
    status: "All",
    tloStatus: "All",
    transcripts: "All",
    startDate: {
      startDate: null,
      endDate: null,
    },
    assignedDate: {
      startDate: null,
      endDate: null,
    },
  });

  filter$: Observable<any> = this.filterSubject.asObservable();
  dashboardApiUrl: string = "";
  prospectsApiUrl: string = "";
  filtersApiUrl: string = "";
  showConsoleLogs: boolean = false;

  private dashboardState: DashboardState | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly envService: AppConfigService,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
  ) {
    this.dashboardApiUrl = envService.mmpDashboardDetailsUrl;
    this.prospectsApiUrl = envService.mmpProspectDetailsUrl;
    this.filtersApiUrl = this.envService.mmpFilterDataUrl;
    this.showConsoleLogs = this.envService.showConsoleLogs;
  }

  updateFilter(filter: any): void {
    this.filterSubject.next(filter);
  }

  getCurrentFilter(): any {
    return this.filterSubject.getValue();
  }

  private buildHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
     // ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    });
  }

  /**
   * Fetches dashboard details from the API.
   * @param apiUrl - The API endpoint URL.
   * @param requestBody - The request payload for the API.
   * @returns An Observable containing the sanitized response.
   */
  fetchDashboardDetails(payload: any): Observable<DashboardResponse> {
    const headers = this.buildHeaders();

    return this.http.post<any>(this.dashboardApiUrl, payload, { headers }).pipe(
      map((response) => this.sanitizeResponse(response)), // Sanitize the response
      map((response) => {
        const result = DashboardResponseSchema.safeParse(response);

        if (!result.success) {
          if (this.showConsoleLogs) {
            console.error("Invalid dashboard response", result.error);
          }
          throw new Error("Invalid dashboard response format");
        }

        // result.data is typed as DashboardResponse
        return result.data;
      }),
    );
  }

  fetchProspectsDetails(combinedId: string): Observable<ProspectDetails> {
    const requestBody = {
      combined_id: combinedId,
    };

    const headers = this.buildHeaders();

    return this.http
      .post<any>(this.prospectsApiUrl, requestBody, { headers })
      .pipe(
        map((response) => this.sanitizeResponse(response)), // Sanitize the response
        map((response) => {
          const result = ProspectDetailsSchema.safeParse(response);
          if (!result.success) {
            if (this.showConsoleLogs) {
              console.error("Invalid prospect details response", result.error);
            }
            throw new Error("Invalid prospect details response format");
          }
          return result.data;
        }),
      );
  }

  /**
   * Sanitizes the API response to prevent security risks.
   * @param response - The API response object.
   * @returns The sanitized response object.
   */
  private sanitizeResponse(response: any): any {
    if (typeof response === "object" && response !== null) {
      for (const key in response) {
        if (response.hasOwnProperty(key) && typeof response[key] === "string") {
          response[key] = response[key]
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;");
        }
      }
    }
    return response;
  }

  setDashboardState(state: DashboardState): void {
    this.dashboardState = state;
  }

  getDashboardState(): DashboardState | null {
    return this.dashboardState;
  }

  fetchDashboardFilters(): Observable<FiltersResponse> {
    const headers = this.buildHeaders();

    return this.http.get<any>(this.filtersApiUrl, { headers }).pipe(
      map((response) => this.sanitizeResponse(response)),
      map((response) => {
        const result = FiltersResponseSchema.safeParse(response);
        if (!result.success) {
          if (this.showConsoleLogs) {
            console.error("Invalid filters response", result.error);
          }
          throw new Error("Invalid filters response format");
        }
        return result.data;
      }),
    );
  }
}
